import { error } from 'cc';
import DataType from '../../../../tableEngine/Script/Game/Data/Config/DataStructure';
import { PlayType } from '../../../../tableEngine/Script/Game/Data/Model/TableModel';
import IHallService from '../../../../tableEngine/Script/Game/Data/Service/IHallService';
import ITableService from '../../../../tableEngine/Script/Game/Data/Service/ITableService';
import HallModel from '../../../../tableEngine/Script/Game/Data/Model/HallModel';
import Lobby from '../../../System/Lobby';
import { LobbyPathName } from '../Config/LobbyPathConfig';
import Delay from '../../../../common/scripts/Toolkit/Utils/Delay';
import CommonGamePreviewData from '../../../../tableEngine/Script/Game/Data/DataObject/PreviewData/CommonGamePreviewData';
import { ILobbyCommonOverviewData, ILobbyCommonStatsData, LobbyOverviewDataType, LobbyStatsDataType } from '../Config/LobbyGamePreviewDataType';
import Parameter from '../../../../common/scripts/Toolkit/Utils/Parameter';
import ILobbyHallService from '../Service/ILobbyHallService';
import LobbySinglePlayGamePreviewData from '../DataObject/PreviewData/LobbySinglePlayGamePreviewData';
import LobbyMultiplePlayGamePreviewData from '../DataObject/PreviewData/LobbyMultiplePlayGamePreviewData';
import LobbyRoundPlayGamePreviewData from '../DataObject/PreviewData/LobbyRoundPlayGamePreviewData';
import parseDisplayName from '../DataObject/PreviewData/CommonToolkit';

enum LobbyHallModelEventType {
    STATS_DATA_RECEIVED = 'STATS_DATA_RECEIVED',
}

export type LobbyGamePreviewData = LobbySinglePlayGamePreviewData | LobbyMultiplePlayGamePreviewData | LobbyRoundPlayGamePreviewData;

// TODO: MSlot (overwrite some part of HallModel with PathConfig)
export default class LobbyHallModel extends HallModel {
    private _blockRequestGameUrl: boolean = false;
    private _hasPlayTypeData: { [key in DataType.Enums.PlayType]?: boolean } = {};

    constructor(
        protected readonly _activatePlayTypes: { [key in PlayType]: boolean },
        protected readonly _hallService: ILobbyHallService & IHallService & ITableService,
    ) {
        super(_activatePlayTypes, _hallService);
    }

    public get blockRequestGameUrl() {
        return this._blockRequestGameUrl;
    }
    public set blockRequestGameUrl(value: boolean) {
        this._blockRequestGameUrl = value;
    }

    public getGameDataByID(hallID: number, gameID: string, playType: DataType.Enums.PlayType = this.currentPlayType): CommonGamePreviewData {
        return this._gameDataMap[CommonGamePreviewData.createKey(hallID, gameID, playType)];
    }

    public init(): void {
        Lobby.connection.once(LobbyPathName.Login, ({ Denom, AccountCurrency }) => {
            this._denom = Denom;
            Lobby.account.currency = AccountCurrency;
        });
        Lobby.connection.once(LobbyPathName.GameAssets, this.onOverviewDataReceived, this);
        Lobby.connection.on(LobbyPathName.HallOverview, this.onOverviewDataReceived, this);
        Lobby.connection.on(LobbyPathName.HallStats, this.onStatsDataReceived, this);
    }

    public async getGameUrl(gameID: string, gameType: DataType.Enums.PlayType): Promise<string> {
        if (this._blockRequestGameUrl) {
            return '';
        }
        this._blockRequestGameUrl = true;
        const url = await this._hallService.getGameUrl(gameID, gameType);
        if (!url) {
            error(`Failed to get game URL for gameID: ${gameID}, gameType: ${gameType}`);
            this._blockRequestGameUrl = false;
            return '';
        }
        const urlObject = new URL(url);
        const hideList = (urlObject.searchParams.get('hide') || '').split(',');
        urlObject.searchParams.append('lang', Lobby.i18n.currentLanguage);
        const autoSwitchBet = Parameter.get('autoSwitchBet', 'false').toLocaleLowerCase() === 'true';
        urlObject.searchParams.append('autoSwitchBet', `${autoSwitchBet}`);
        urlObject.searchParams.append('nf', `${Lobby.config.numberFormatterMode}`);
        urlObject.searchParams.append('brandTheme', `${Lobby.config.theme}`);
        if (Lobby.config.hideDollar) {
            if (!hideList.includes('dollar')) {
                hideList.push(`dollar`);
            }
        } else if (hideList.includes('dollar')) {
            const index = hideList.findIndex((item) => item === 'dollar');
            if (index > -1) {
                hideList.splice(index, 1);
            }
        }
        urlObject.searchParams.append('hide', hideList.join(','));
        this._blockRequestGameUrl = false;
        return urlObject.href;
    }

    public requestOverviewData() {
        Lobby.connection.send(LobbyPathName.HallOverview, { GameType: `${this.currentPlayType}_play` });
    }

    public waitForHallOverviewData() {
        if (this._hasPlayTypeData[this.currentPlayType]) {
            return Promise.resolve();
        }
        return Delay.event(HallModel.EVENT_TYPE.OVERVIEW_DATA_RECEIVED, this);
    }

    public waitForHallStatsData() {
        return Delay.event(LobbyHallModelEventType.STATS_DATA_RECEIVED, this);
    }

    public updatePlayType(nextPlayType: DataType.Enums.PlayType) {
        this._currentPlayType = nextPlayType;
    }

    protected onOverviewDataReceived(data: DataType.Hall.OverviewResponseData) {
        if (!data) {
            error('Overview data is null or undefined');
            return;
        }
        const previewDatas = this.createPreviewDataObjectByOverview(data);
        previewDatas.forEach((previewData) => {
            const { HallID: hallID, GameID: gameID, GameType: gameType } = previewData;
            const gameKey = CommonGamePreviewData.createKey(hallID, gameID, gameType);
            this._gameAssets[gameKey] = previewData.Asset;
            if (!this._gameDataMap[gameKey]) {
                this._gameDataMap[gameKey] = this._hallService.getGamePreviewData(hallID, gameID, gameType, previewData);
            } else {
                if (!this._hasPlayTypeData[gameType]) {
                    this._hasPlayTypeData[gameType] = true;
                }
                (this._gameDataMap[gameKey] as CommonGamePreviewData).updateOverviewData(previewData);
            }
        });
        this.emit(HallModel.EVENT_TYPE.OVERVIEW_DATA_RECEIVED);
    }

    protected onStatsDataReceived(data: DataType.Hall.StatsResponseData) {
        if (!data) {
            error('Stats data is null or undefined');
            return;
        }
        const hallID = this.currentHallID;
        const previewStatsDatas = this.createPreviewDataByStats(data);
        previewStatsDatas.forEach((statsData) => {
            const { GameID: gameID, GameType: gameType } = statsData;
            const gameKey = CommonGamePreviewData.createKey(hallID, gameID, gameType);
            this._gameDataMap[gameKey].updateStatsData(statsData);
        });
        this.emit(LobbyHallModelEventType.STATS_DATA_RECEIVED);
    }

    protected createPreviewDataObjectByOverview(overviewRawData: DataType.Hall.OverviewResponseData): LobbyOverviewDataType[] {
        const { LobbyID = 1, Games } = overviewRawData;
        this._currentHallID = LobbyID;
        const dataList = Games.map((game) => {
            const playType = this._getPlayType(game.GameType);
            const commonPrviewData: ILobbyCommonOverviewData = {
                HallID: this._currentHallID,
                GameID: game.GameID,
                GameType: playType,
                Enabled: game.IsEnabled ?? false,
                Asset: {
                    Logo: game.ImageURL,
                },
                DisplayName: parseDisplayName(game.DisplayName),
                Name: game.Name,
                IsFavorite: game.IsFavorite ?? false,
            };
            let previewData: LobbyOverviewDataType = null;
            if (playType === DataType.Enums.PlayType.Single) {
                previewData = {
                    ...commonPrviewData,
                    TableCount: 0,
                };
            } else if (playType === DataType.Enums.PlayType.Multiple) {
                previewData = {
                    ...commonPrviewData,
                };
            } else if (playType === DataType.Enums.PlayType.Round) {
                previewData = {
                    ...commonPrviewData,
                };
            }
            return previewData;
        });
        return dataList;
    }

    protected createPreviewDataByStats(originRawData: DataType.Hall.StatsResponseData): LobbyStatsDataType[] {
        const { S7Stats, WinSlotStats } = originRawData;
        const s7DataList =
            S7Stats?.map((stats) => {
                const playType = this._getPlayType(stats.GameType);
                const commonStatsData: ILobbyCommonStatsData = {
                    GameID: stats.GameID,
                    GameType: playType,
                };
                let statsData: LobbyStatsDataType = null;
                if (playType === DataType.Enums.PlayType.Single) {
                    statsData = {
                        ...commonStatsData,
                        MachineCount: stats.MachineCount,
                        IdleMachineCount: stats.IdleMachineCount,
                        OutOfService: stats.OutOfService,
                        ProgressiveValue: stats.ProgressiveValue,
                    };
                }
                return statsData;
            }) ?? [];

        const winslotDataList =
            WinSlotStats?.map((stats) => {
                const playType = this._getPlayType(stats.GameType);
                const commonStatsData: ILobbyCommonStatsData = {
                    GameID: stats.GameID,
                    GameType: playType,
                };
                let statsData: LobbyStatsDataType = null;
                if (playType === DataType.Enums.PlayType.Multiple) {
                    statsData = {
                        ...commonStatsData,
                        TotalBet: stats.TotalBet,
                        PlayerCount: stats.PlayerCount,
                        RTP: stats.RTP,
                        ProgressiveValue: stats.ProgressiveValue,
                    };
                } else if (playType === DataType.Enums.PlayType.Round) {
                    statsData = {
                        ...commonStatsData,
                        TotalBet: stats.TotalBet,
                        PlayerCount: stats.PlayerCount,
                        RTP: stats.RTP,
                        ProgressiveValue: stats.ProgressiveValue,
                    };
                }
                return statsData;
            }) ?? [];
        const dataList = [...s7DataList, ...winslotDataList];
        return dataList;
    }

    private _getPlayType(gameType: DataType.Enums.LobbyGameType): DataType.Enums.PlayType {
        return gameType.replace(/_play$/, '') as DataType.Enums.PlayType;
    }
}
